import { useGridFilter } from 'ag-grid-react';
import { ChangeEvent, useCallback, useId, useState } from 'react';
import { Button, Card, FormCheck, Stack } from 'react-bootstrap';

import { ResultTypeLegend } from '@components/results';
import type {
  AgGridAfterGuiAttachedCallback,
  AgGridCustomFilterProps,
  AgGridDoesFilterPassCallback,
} from '@customTypes/ag-grid';
import { RESULT_TYPE_CONFIG } from '@helpers/order';

export const ResultFilter: React.FC<AgGridCustomFilterProps> = props => {
  const { model, reactContainer, getValue, onModelChange } = props;

  const [closeFilter, setCloseFilter] = useState<(() => void) | undefined>();
  const [selectedOption, setSelectedOption] = useState<string | null>(props.model?.filter);
  const headerId = useId();

  const afterGuiAttached = useCallback<AgGridAfterGuiAttachedCallback>(params => {
    setCloseFilter(() => params?.hidePopup);
  }, []);

  // This is not used when doing server-side filtering, and is only configured as a fallback
  const doesFilterPass = useCallback<AgGridDoesFilterPassCallback>(
    params => model === getValue(params.node),
    [model, getValue],
  );

  useGridFilter({
    afterGuiAttached,
    doesFilterPass,
  });

  const onFilterApply = () => {
    onModelChange({
      filter: selectedOption,
      filterType: 'text',
      type: 'equals',
    });

    if (closeFilter) {
      closeFilter();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  if (reactContainer) {
    const menuElement = reactContainer.closest('.ag-menu');
    menuElement?.removeAttribute('aria-label');
    menuElement?.setAttribute('aria-labelledby', headerId);
  }

  return (
    <Card>
      <Card.Header id={headerId} as='h3' className='h6'>
        Filter by Result
      </Card.Header>

      <Card.Body>
        <Stack className='gap-3 filter-body'>
          <Stack>
            <FormCheck
              type='radio'
              name='result'
              label='All'
              id='filter-result-all'
              checked={!selectedOption}
              autoFocus={!selectedOption}
              onChange={() => setSelectedOption(null)}
            />

            {Object.entries(RESULT_TYPE_CONFIG).map(([resultType, config]) => (
              <FormCheck
                key={resultType}
                type='radio'
                name='result'
                label={config.label}
                id={`filter-result-${resultType}`}
                value={resultType}
                checked={selectedOption === resultType}
                autoFocus={selectedOption === resultType}
                onChange={handleChange}
              />
            ))}
          </Stack>

          <div className='d-flex justify-content-end pt-3 border-top'>
            <Button size='sm' onClick={onFilterApply}>
              Apply
            </Button>
          </div>
        </Stack>
      </Card.Body>

      <Card.Footer>
        <small>
          <ResultTypeLegend />
        </small>
      </Card.Footer>
    </Card>
  );
};
